import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { DocumentDetailsStore } from '@module-instance/dynamic-module/document-details/common';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { CipoStateComponent } from '@shared/components/cipo-state/state.component';
import { EntityTableRow } from '@shared/components/data-list/common';
import { CipoTextComponent } from '@shared/components/fields/cipo-text/cipo-text.component';
import { CipoFieldTypes, CipoTextConfig } from '@shared/components/fields/common';
import { CipoKeyboardListDirective } from '@shared/components/virtual-list/keyboard-list.directive';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { PipesModule } from '@shared/modules/pipes.module';
import { UserStore } from '@signal-store/index';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs';

const SCROLL_THRESHOLD = 150;

@Component({
  selector: 'cipo-document-sidebar',
  imports: [
    CipoTextComponent,
    CipoStateComponent,
    NgxSkeletonLoaderModule,
    MatIconModule,
    MatButtonModule,
    FlexLayoutModule,
    ScrollingModule,
    TranslateModule,
    PipesModule,
    MatTooltipModule,
    CipoKeyboardListDirective,
  ],
  templateUrl: './document-sidebar.component.html',
  styleUrl: './document-sidebar.component.scss',
})
export class DocumentSidebarComponent implements OnInit {
  readonly FieldTypeEnum = FieldTypeEnum;
  userStore = inject(UserStore);
  dmStore = inject(DocumentDetailsStore);
  viewport = viewChild(CdkVirtualScrollViewport);
  private searchField = viewChild<CipoTextComponent>('searchField');
  private toggleBtn = viewChild('toggleBtn');

  selectedEntityInstanceId = input<number>();
  allDisable = input<boolean>(false);
  selectDocument = output<EntityTableRow>();

  lastLoadedPage = 0;
  /* We only show the sidebar for the active contract and module */
  contractId = this.userStore.contract().id;
  moduleId = this.userStore.menu.activeMenuItem().moduleId;
  items = computed(() => this.dmStore.sidebar.items());
  descriptionField = computed(() => this.dmStore.sidebar.descriptionField());
  searchControl = new FormControl(this.dmStore.sidebar.searchViewModel()?.criteria || '');
  fieldData = signal<CipoTextConfig>({
    type: CipoFieldTypes.Text,
    label: 'general.search',
    suffixIcon: 'search',
  });
  sidebarOpened = signal(false);
  isLoading = signal(false);

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.default), distinctUntilChanged())
      .subscribe(criteria => this.loadStoreItems(0, criteria));

    effect(() => {
      if (this.selectedEntityInstanceId()) {
        this.setIndicatorPosition(untracked(this.items));
      }
    });
  }

  ngOnInit(): void {
    this.loadStoreItems(0);
  }

  /** This method is called once, when the sidebarItems are loaded */
  private setIndicatorPosition(sidebarItems: EntityTableRow[]) {
    const index = sidebarItems.findIndex(item => item.entity_instance_id === this.selectedEntityInstanceId());
    if (index === -1) {
      return;
    }
    setTimeout(() => {
      /* We need this timeout to wait for the indicator to be positioned */
      this.viewport().scrollToIndex(index, 'smooth');
    }, 1000);
  }

  toggleSidebar() {
    this.sidebarOpened.update(opened => !opened);
    if (this.sidebarOpened()) {
      setTimeout(() => this.searchField()?.textInput()?.nativeElement?.focus());
    } else {
      (this.toggleBtn() as ElementRef<HTMLButtonElement>)?.nativeElement?.focus();
    }
    this.setIndicatorPosition(this.items());
  }

  documentClick(item: EntityTableRow) {
    if (this.allDisable() || item.entity_instance_id === this.selectedEntityInstanceId()) {
      return;
    }
    this.selectDocument.emit(item);
  }

  loadStoreItems(page: number, criteria: string = this.searchControl.value) {
    this.isLoading.set(true);
    this.dmStore
      .loadSidebarItems({
        page,
        criteria,
        moduleId: this.moduleId,
        contractId: this.contractId,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(({ data }) => {
        this.lastLoadedPage = page;
        if (page === 0) {
          this.setIndicatorPosition(data);
        }
      });
  }

  onScroll() {
    if (this.items().length === this.dmStore.sidebar.records()) {
      return;
    }
    const scrollOffset = this.viewport()?.measureScrollOffset('bottom');
    if (scrollOffset < SCROLL_THRESHOLD && !this.isLoading()) {
      this.loadStoreItems(this.lastLoadedPage + 1);
    }
  }

  virtualTrackBy(index: number, item: EntityTableRow): string | number {
    return item.full_doc_num;
  }
}
